import { sectionConfig as accountingSectionConfig } from '@/features/accounting/accounting.config'
import { sectionConfig as adminSectionConfig } from '@/features/admin/admin.config'
import { sectionConfig as clientSuccessSectionConfig } from '@/features/client-success/client-success.config'
import { sectionConfig as deploymentsSectionConfig } from '@/features/deployments/deployments.config'
import { sectionConfig as designSectionConfig } from '@/features/design/design.config'
import { sectionConfig as dispatchingSectionConfig } from '@/features/dispatching/dispatching.config'
import { sectionConfig as internalSectionConfig } from '@/features/internal/internal.config'
import { sectionConfig as knowledgeSectionConfig } from '@/features/knowledge/knowledge.config'
import { sectionConfig as logisticsSectionConfig } from '@/features/logistics/logistics.config'
import { sectionConfig as meSectionConfig } from '@/features/me/me.config'
import { sectionConfig as metricsSectionConfig } from '@/features/metrics/metrics.config'
import { sectionConfig as portalSectionConfig } from '@/features/portal/portal.config'
import { sectionConfig as salesSectionConfig } from '@/features/sales/sales.config'
import { sectionConfig as serviceCenterConfig } from '@/features/service-center/service-center.config'
import { sectionConfig as technicianSectionConfig } from '@/features/technician/technician.config'
import { type SectionConfig } from '@/utils/get-matched-section'

/**
 * Every app's route config, in one place.
 *
 * WHY THIS EXISTS: there used to be two of these lists — one in
 * `site-header.tsx` (feeding `getMatchedSection` for breadcrumbs) and one in
 * `resolve-section-match.ts` (feeding `usePageTitle` for document.title).
 * Registering an app meant remembering both, and forgetting either was silent:
 * omit it from the header map and the breadcrumb trail renders as the bare app
 * name with no page segment; omit it from the title list and document.title
 * falls back to a humanized URL segment. Neither is a type error or a lint
 * failure, and I hit both while adding the `me` app.
 *
 * `logistics` and `accounting` were missing from BOTH — they had been running on
 * the adminConfig fallback and humanized titles. Adding them here is what fixed
 * that, which is the whole reason to have one list.
 *
 * ADDING AN APP: one entry. The key is the app key, which is also the path
 * segment `getMatchedSection` strips as its `basePrefix`.
 */
export const APP_SECTION_CONFIGS: Record<string, SectionConfig> = {
  'service-center': serviceCenterConfig,
  internal: internalSectionConfig,
  portal: portalSectionConfig,
  admin: adminSectionConfig,
  sales: salesSectionConfig,
  dispatching: dispatchingSectionConfig,
  technician: technicianSectionConfig,
  deployments: deploymentsSectionConfig,
  accounting: accountingSectionConfig,
  logistics: logisticsSectionConfig,
  design: designSectionConfig,
  metrics: metricsSectionConfig,
  knowledge: knowledgeSectionConfig,
  'client-success': clientSuccessSectionConfig,
  me: meSectionConfig
}

/**
 * The lookup order `usePageTitle` walks when it has only a pathname and must
 * discover which app owns it. First match wins.
 *
 * Order matters only for correctness against overlapping route shapes; it is
 * otherwise the declaration order above.
 */
export const APP_SECTION_LOOKUP: ReadonlyArray<
  readonly [SectionConfig, string]
> = Object.entries(APP_SECTION_CONFIGS).map(
  ([prefix, config]) => [config, prefix] as const
)
